/**
 * Deposit Queue Tracker
 *
 * Utilities to track the status of validator deposits through the beacon chain queue.
 * This addresses the issue where the launchpad shows "No pending deposits" even when
 * deposits are confirmed on the execution layer but not yet processed by the beacon chain.
 */

// Constants for parsing deposit event data
const HEX_PREFIX_LENGTH = 2;
const DEPOSIT_INDEX_BYTES = 8;
const DEPOSIT_INDEX_HEX_CHARS = DEPOSIT_INDEX_BYTES * 2;

// DepositEvent signature: keccak256("DepositEvent(bytes,bytes,bytes,bytes,bytes)")
const DEPOSIT_EVENT_SIGNATURE =
  '0x649bbc62d0e31342afea4e5cd82d4049e7e1ee912fc0889aa790803be39038c5';

/**
 * Known beacon chain validator statuses from the Beacon API.
 * @see https://ethereum.github.io/beacon-APIs/#/Beacon/getStateValidator
 */
type BeaconValidatorStatus =
  | 'pending_initialized'
  | 'pending_queued'
  | 'active_ongoing'
  | 'active_exiting'
  | 'active_slashed'
  | 'exited_unslashed'
  | 'exited_slashed'
  | 'withdrawal_possible'
  | 'withdrawal_done'
  | 'waiting_for_eligibility';

export enum BeaconDepositStatus {
  UNKNOWN = 'UNKNOWN',
  TX_FAILED = 'TX_FAILED',
  IN_QUEUE = 'IN_QUEUE',
  NEXT_IN_QUEUE = 'NEXT_IN_QUEUE',
  PENDING_ACTIVATION = 'PENDING_ACTIVATION',
  ACTIVE = 'ACTIVE',
  BEACON_UNAVAILABLE = 'BEACON_UNAVAILABLE',
}

export interface ValidatorStatusResult {
  status: BeaconDepositStatus;
  validatorIndex: number | null;
  balance: string | null;
}

export interface DepositQueueStatus {
  status: BeaconDepositStatus;
  depositIndex: number | null;
  beaconProcessingIndex: number | null;
  queuePosition: number | null;
  validatorIndex: number | null;
  balance: string | null;
  error: string | null;
}

export interface DepositQueueConfig {
  beaconRpcUrl: string;
  executionRpcUrl: string;
  depositContractAddress: string;
  signal?: AbortSignal;
}

/**
 * Get the current deposit count from the beacon chain.
 * This is the total number of deposits that have been processed by the beacon chain,
 * which equals the next deposit index to be processed.
 */
export async function getBeaconDepositIndex(
  beaconRpcUrl: string,
  signal?: AbortSignal
): Promise<number | null> {
  try {
    // Get eth1_data from the latest block which contains deposit_count
    const response = await fetch(`${beaconRpcUrl}/eth/v2/beacon/blocks/head`, {
      signal,
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    const depositCount = data.data?.message?.body?.eth1_data?.deposit_count;

    if (!depositCount) {
      return null;
    }

    return parseInt(depositCount, 10);
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      const message = error instanceof Error ? error.message : String(error);
      // eslint-disable-next-line no-console
      console.debug('getBeaconDepositIndex failed:', message);
    }
    return null;
  }
}

/**
 * Parse the deposit index from a transaction receipt's deposit event.
 * The deposit index is stored in the last 8 bytes of the event data in little-endian format.
 */
export async function getDepositIndexFromTxReceipt(
  rpcUrl: string,
  txHash: string,
  depositContractAddress: string,
  signal?: AbortSignal
): Promise<number | null> {
  try {
    const response = await fetch(rpcUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        method: 'eth_getTransactionReceipt',
        params: [txHash],
        id: 1,
      }),
      signal,
    });

    if (!response.ok) {
      return null;
    }

    const json = await response.json();
    const receipt = json.result;

    if (!receipt) {
      return null;
    }

    // Check if transaction succeeded
    if (receipt.status !== '0x1') {
      return null;
    }

    // Find the deposit event
    const depositEvent = receipt.logs.find(
      (log: { address: string; topics: string[] }) =>
        log.address.toLowerCase() === depositContractAddress.toLowerCase() &&
        log.topics[0] === DEPOSIT_EVENT_SIGNATURE
    );

    if (!depositEvent) {
      return null;
    }

    // The deposit index is in the last 8 bytes of the data field (little-endian)
    // Data structure: pubkey (48) + withdrawal_credentials (32) + amount (8) + signature (96) + index (8) = 192 bytes
    // But the data is ABI encoded with offsets, so we need the last 8 bytes
    const data = depositEvent.data.slice(HEX_PREFIX_LENGTH);
    const indexHex = data.slice(-DEPOSIT_INDEX_HEX_CHARS);

    // Convert little-endian hex to number
    const indexBytes = indexHex.match(/.{2}/g);
    if (!indexBytes) {
      return null;
    }

    const indexBigInt = indexBytes.reduce(
      (acc: bigint, byte: string, i: number) =>
        acc + BigInt(parseInt(byte, 16)) * BigInt(256 ** i),
      BigInt(0)
    );

    return Number(indexBigInt);
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      const message = error instanceof Error ? error.message : String(error);
      // eslint-disable-next-line no-console
      console.debug('getDepositIndexFromTxReceipt failed:', message);
    }
    return null;
  }
}

/**
 * Map beacon chain validator status to our deposit status enum.
 */
function mapBeaconStatus(
  beaconStatus: BeaconValidatorStatus
): BeaconDepositStatus {
  if (beaconStatus.startsWith('active')) {
    return BeaconDepositStatus.ACTIVE;
  }
  if (
    beaconStatus.startsWith('pending') ||
    beaconStatus === 'waiting_for_eligibility'
  ) {
    return BeaconDepositStatus.PENDING_ACTIVATION;
  }
  return BeaconDepositStatus.UNKNOWN;
}

/**
 * Check the status of a validator on the beacon chain by public key.
 */
export async function checkValidatorStatus(
  beaconRpcUrl: string,
  pubkey: string,
  signal?: AbortSignal
): Promise<ValidatorStatusResult> {
  try {
    const normalizedPubkey = pubkey.startsWith('0x') ? pubkey : `0x${pubkey}`;
    const response = await fetch(
      `${beaconRpcUrl}/eth/v1/beacon/states/head/validators/${normalizedPubkey}`,
      { signal }
    );

    if (!response.ok) {
      return {
        status: BeaconDepositStatus.UNKNOWN,
        validatorIndex: null,
        balance: null,
      };
    }

    const data = await response.json();
    const validatorData = data?.data;

    if (!validatorData?.index || !validatorData?.validator) {
      return {
        status: BeaconDepositStatus.UNKNOWN,
        validatorIndex: null,
        balance: null,
      };
    }

    const validatorIndex = parseInt(validatorData.index, 10);
    const balance = validatorData.validator.effective_balance;
    const beaconStatus = validatorData.status as BeaconValidatorStatus;

    return {
      status: mapBeaconStatus(beaconStatus),
      validatorIndex,
      balance,
    };
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      const message = error instanceof Error ? error.message : String(error);
      // eslint-disable-next-line no-console
      console.debug('checkValidatorStatus failed:', message);
    }
    return {
      status: BeaconDepositStatus.UNKNOWN,
      validatorIndex: null,
      balance: null,
    };
  }
}

/**
 * Get comprehensive deposit queue status for a transaction.
 * This is the main function to call after a deposit transaction succeeds.
 */
export async function getDepositQueueStatus(
  txHash: string,
  pubkey: string,
  config: DepositQueueConfig
): Promise<DepositQueueStatus> {
  const { signal } = config;

  // Step 1: Get deposit index from transaction receipt
  const depositIndex = await getDepositIndexFromTxReceipt(
    config.executionRpcUrl,
    txHash,
    config.depositContractAddress,
    signal
  );

  if (depositIndex === null) {
    return {
      status: BeaconDepositStatus.TX_FAILED,
      depositIndex: null,
      beaconProcessingIndex: null,
      queuePosition: null,
      validatorIndex: null,
      balance: null,
      error: 'Transaction failed or deposit event not found',
    };
  }

  // Step 2: Get beacon chain's current processing index
  const beaconProcessingIndex = await getBeaconDepositIndex(
    config.beaconRpcUrl,
    signal
  );

  if (beaconProcessingIndex === null) {
    return {
      status: BeaconDepositStatus.BEACON_UNAVAILABLE,
      depositIndex,
      beaconProcessingIndex: null,
      queuePosition: null,
      validatorIndex: null,
      balance: null,
      error: 'Unable to fetch beacon chain status',
    };
  }

  // Step 3: Calculate queue position
  const queuePosition = depositIndex - beaconProcessingIndex;

  // Step 4: Check validator status on beacon chain
  const validatorStatus = await checkValidatorStatus(
    config.beaconRpcUrl,
    pubkey,
    signal
  );

  // Step 5: Determine final status
  let finalStatus: BeaconDepositStatus;

  if (validatorStatus.status === BeaconDepositStatus.ACTIVE) {
    finalStatus = BeaconDepositStatus.ACTIVE;
  } else if (
    validatorStatus.status === BeaconDepositStatus.PENDING_ACTIVATION
  ) {
    finalStatus = BeaconDepositStatus.PENDING_ACTIVATION;
  } else if (queuePosition === 0) {
    finalStatus = BeaconDepositStatus.NEXT_IN_QUEUE;
  } else if (queuePosition > 0) {
    finalStatus = BeaconDepositStatus.IN_QUEUE;
  } else {
    // queuePosition < 0 means beacon has processed past our deposit
    // but validator not found - might still be processing
    finalStatus = BeaconDepositStatus.PENDING_ACTIVATION;
  }

  return {
    status: finalStatus,
    depositIndex,
    beaconProcessingIndex,
    queuePosition,
    validatorIndex: validatorStatus.validatorIndex,
    balance: validatorStatus.balance,
    error: null,
  };
}
